import React from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

interface MobileCardProps {
  children: React.ReactNode;
  className?: string;
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  onClick?: () => void;
  header?: React.ReactNode;
}

const MobileCard: React.FC<MobileCardProps> = ({
  children,
  className,
  title,
  subtitle,
  action,
  onClick,
  header
}) => {
  return (
    <Card 
      className={cn(
        "w-full transition-all duration-200",
        onClick && "cursor-pointer hover:shadow-md active:scale-[0.98]",
        className
      )}
      onClick={onClick}
    >
      {(title || subtitle || action || header) && (
        <CardHeader className="pb-2 px-4 pt-4">
          {header ? header : (
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                {title && (
                  <h3 className="font-semibold text-base leading-6 truncate">
                    {title}
                  </h3>
                )}
                {subtitle && (
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                    {subtitle}
                  </p>
                )}
              </div>
              {action && (
                <div className="ml-3 flex-shrink-0">
                  {action}
                </div>
              )}
            </div>
          )}
        </CardHeader>
      )}
      <CardContent className={cn(
        "px-4 pb-4", 
        title || subtitle || action || header ? "pt-0" : "pt-4"
      )}>
        {children}
      </CardContent>
    </Card>
  );
};

// Specialized card for lists
interface MobileListCardProps {
  items: Array<{
    id: string;
    title: string;
    subtitle?: string;
    badge?: string | number;
    icon?: React.ReactNode;
    action?: React.ReactNode;
    onClick?: () => void;
  }>;
  className?: string;
}

export const MobileListCard: React.FC<MobileListCardProps> = ({
  items,
  className
}) => {
  return (
    <Card className={cn("w-full", className)}>
      <CardContent className="p-0">
        <div className="divide-y divide-border">
          {items.map((item, index) => (
            <div
              key={item.id}
              className={cn(
                "flex items-center gap-3 p-4 transition-colors",
                item.onClick && "cursor-pointer hover:bg-muted/50 active:bg-muted"
              )}
              onClick={item.onClick}
            >
              {item.icon && (
                <div className="flex-shrink-0">
                  {item.icon}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm truncate">
                    {item.title}
                  </span>
                  {item.badge && (
                    <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {item.badge}
                    </span>
                  )}
                </div>
                {item.subtitle && (
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                    {item.subtitle}
                  </p>
                )}
              </div>
              {item.action && (
                <div className="flex-shrink-0">
                  {item.action}
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default MobileCard;
